import InsertLinkIcon from '@mui/icons-material/InsertLink';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { Box, Button } from '@mui/material';
import { FC, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useQuery } from 'react-query';
import { Outlet, useParams } from 'react-router-dom';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import MonitorIcon from '@mui/icons-material/Monitor';
import PostAddIcon from '@mui/icons-material/PostAdd';

import { Loader } from '@atoms';
import { ROUTES } from '@constants';
import { useModalState } from '@hooks';
import { AccountHeader, Tabs } from '@molecules';
import { InnerView } from '@templates';

import { ApiClientEnterprise } from '@helpers/apiClients';
import { CreateNewEnterpriseUsersModal } from '@organisms/Modals/CreateNewEnterpriseUsersModal';
import { API_USERS_URL } from '@organisms/Tables/Enterprise/UsersTable/constants';
import { IOrganizationsDetailData } from '@services';
import { StyledDisabledButton } from './EnterpriseTabs.styles';
import { useEnterpriseContext } from '@contexts/Enterprise/EnterpriseProvider';
import { AddNewsWireModal } from 'src/features/Enterprise/NewsWire/AddNewsWireModal/AddNewsWireModal';
import { AddSentimentAnalysisModal } from 'src/features/Enterprise/SentimentAnalysis/AddSentimentAnalysisModal/AddSentimentAnalysisModal';
import {
  feedbackCategoryOptions,
  feedbackSubCategoryOptions,
} from 'src/features/Enterprise/SentimentAnalysis/utils';
import { AddMonitoringModal } from 'src/features/Enterprise/Monitoring/AddMonitoringModal/AddMonitoringModal';
import UploadCSVModal from '@organisms/Enterprise/LinksManagement/UploadCSVModal/UploadCSVModal';

const links = [
  { label: 'Users', to: ROUTES.Users },
  { label: 'Links Review', to: ROUTES.LinksReview },
  { label: 'Links Management', to: ROUTES.LinksManagement },
  { label: 'Monitoring', to: ROUTES.Monitoring },
  { label: 'Newswire', to: ROUTES.NewsWire },
  { label: 'Sentiment analysis', to: ROUTES.SentimentAnalysis },
  // TODO: temporarily hide for June 30th release
  // { label: 'Status', to: ROUTES.Status },
  // { label: 'Scan Settings', to: ROUTES.ScanSettings },
  // { label: 'Documents', to: ROUTES.Documents },
];

const PARAMS_ID = 'enterprise-tabs';

export const EnterpriseTabs: FC<{ hideTabs?: boolean }> = ({ hideTabs }) => {
  const [openUserModal, , onOpenUserModal, onCloseUserModal] = useModalState(false);
  const [openUploadCSVModal, , onOpenUploadCSVModal, onCloseUploadCSVModal] = useModalState(false);
  const [openMonitoringModal, , onOpenMonitoringModal, onCloseMonitoringModal] =
    useModalState(false);
  const [openNewswireModal, , onOpenNewswireModal, onCloseNewswireModal] = useModalState(false);
  const [
    openSentimentAnalysisModal,
    ,
    onOpenSentimentAnalysisModal,
    onCloseSentimentAnalysisModal,
  ] = useModalState(false);

  const { setOrganizationDetail } = useEnterpriseContext();

  const { id } = useParams();

  const [tabIndex, setTabIndex] = useState<number>(0);

  const { data, isLoading } = useQuery<unknown, unknown, IOrganizationsDetailData>(
    `${API_USERS_URL.GET_DETAILS_ORGANIZATION}/${id}`,
    () =>
      ApiClientEnterprise.get(`${API_USERS_URL.GET_DETAILS_ORGANIZATION}/${id}`).then(
        (res) => res.data,
      ),
  );

  const companyName = useMemo(() => {
    if (data) {
      setOrganizationDetail(data.result);
      return data.result.name || '';
    }
    return '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const _renderHeaderButtons = () => {
    const link = links[tabIndex];

    switch (link.to) {
      case ROUTES.Users:
        return (
          <Box>
            <Button variant="contained" startIcon={<PersonAddIcon />} onClick={onOpenUserModal}>
              Add Users
            </Button>
            <StyledDisabledButton
              disabled
              sx={{ marginLeft: '12px' }}
              variant="contained"
              startIcon={<InsertLinkIcon />}
            >
              Upload Links
            </StyledDisabledButton>
          </Box>
        );
      case ROUTES.LinksManagement:
        return (
          <Button variant="contained" startIcon={<PostAddIcon />} onClick={onOpenUploadCSVModal}>
            Upload links
          </Button>
        );
      case ROUTES.Monitoring:
        return (
          <Button variant="contained" startIcon={<MonitorIcon />} onClick={onOpenMonitoringModal}>
            Add Monitoring
          </Button>
        );
      case ROUTES.NewsWire:
        return (
          <Button variant="contained" startIcon={<AutoGraphIcon />} onClick={onOpenNewswireModal}>
            Add Newswire
          </Button>
        );
      case ROUTES.SentimentAnalysis:
        return (
          <Button
            variant="contained"
            startIcon={<AutoGraphIcon />}
            onClick={onOpenSentimentAnalysisModal}
          >
            Add Analysis
          </Button>
        );
      default:
        return null;
    }
  };

  const onChangeTab = (tabIndex: number) => setTabIndex(tabIndex);

  return (
    <Loader isLoading={isLoading}>
      <>
        {companyName && (
          <Helmet>
            <title>{companyName}</title>
          </Helmet>
        )}
        <AccountHeader
          name={companyName || ''}
          paramsId={PARAMS_ID}
          route={`/${ROUTES.Accounts}/${ROUTES.ContentCreators}`}
          customSxName={{
            textOverflow: 'ellipsis',
            width: '350px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
          isDisplayNameWithTooltip={companyName.length > 20}
        >
          {_renderHeaderButtons()}
        </AccountHeader>
        <InnerView autoHeight>
          {!hideTabs && (
            <Box mt="20px" mb="24px">
              <Tabs links={links} modified={data?.result.modifiedAt} onChange={onChangeTab} />
            </Box>
          )}
          <Outlet />
        </InnerView>
        <CreateNewEnterpriseUsersModal id={id} open={openUserModal} onClose={onCloseUserModal} />
        <UploadCSVModal open={openUploadCSVModal} onClose={onCloseUploadCSVModal} />
        <AddMonitoringModal
          open={openMonitoringModal}
          onClose={onCloseMonitoringModal}
          title="Add asset item"
          subtitle="Here you can add new asset item"
        />
        <AddNewsWireModal
          open={openNewswireModal}
          onClose={onCloseNewswireModal}
          title="Add newswire"
          subtitle="Here you can add new newswire"
        />
        <AddSentimentAnalysisModal
          open={openSentimentAnalysisModal}
          onClose={onCloseSentimentAnalysisModal}
          title="Add Analysis"
          subtitle="Here you can add new Analysis"
          categories={feedbackCategoryOptions}
          subCategories={feedbackSubCategoryOptions}
        />
      </>
    </Loader>
  );
};
